/**
 * The standing-idle library (lane C0-POSE-FIX, the user 2026-10-10: "standing differs for a man, a woman, an elder and
 * a child, with many standing poses, and NPCs pick one at random"). Research: docs/research/C0_POSES.md §1.
 *
 * Each idle is a short loop of 8–12 key poses in skeleton terms: a stance laid over the standing pose (feet apart, the
 * leg that takes the weight with the hips over it and the free hip dropped, the shoulders tipped against the hips and
 * turned, the spine pitched, the breath lifting the chest; rig/skeleton.ts `Stance`) plus the arms, head and feet.
 * The keys are sampled from one smooth function of the loop's time, so the weight drifts and the chest breathes on a
 * curve, then blended on the Catmull-Rom spline like every motion.
 *
 * Frames: a loop is two motions of `FRAMES` frames each (`idl:<id>:a`, `idl:<id>:b`), the halves of the keys, each
 * told the key before and after it so the spline runs on through the join; the renderer plays 26 frames a loop
 * (render/idleLib.ts), about four seconds.
 */
import type { Stance } from '../rig/skeleton';
import type { FarArm, MotionDef, NearArm, PoseRig, Rig } from './kit';
import type { Pose } from './index';

export type IdleGroup = 'man' | 'woman' | 'elder' | 'child';
export const IDLE_GROUPS: readonly [IdleGroup, string][] = [
  ['man', 'ชาย (ชาวยุทธ์)'],
  ['woman', 'หญิง (ชาวยุทธ์)'],
  ['elder', 'ผู้เฒ่า'],
  ['child', 'เด็ก'],
];

/** One moment of an idle: the stance and the rest of the body. */
interface Moment {
  /** The weight: +1 all on the right leg (A), −1 on the left (B), 0 both. */
  w: number;
  /** Feet apart (as `Bearing.stance`), the free foot set forward, the shoulders' turn, the spine's pitch. */
  feet: number;
  step: number;
  turn: number;
  tilt: number;
  /** The chest lifted by the breath (design px). */
  breath: number;
  /** How far hanging hands keep from the body. */
  tuck: number;
  rest: 'side' | 'front' | 'back';
  handB?: 'hip' | 'hilt' | 'forward';
  arm?: NearArm;
  far?: FarArm;
  /** Hand targets (classic design coordinates, as `POSE_HANDS`), for an arm that is not hanging. */
  A?: [number, number];
  B?: [number, number];
  o: number;
  lean: number;
  headDx: number;
  sway: number;
  armSwing: number;
  closed: boolean;
  /** Feet placed by the idle itself (a kick, a tapping toe) instead of from the stance. */
  f?: [number, number];
  /** The upper back rounded forward (an elder's stoop), design px. */
  hunch: number;
  /** The right hand placed at the face (stroking the beard, a hand to the mouth), from the head as it stands. */
  anchorA?: Anchor;
  /** A tool held that the pose's name does not say (a cane; rig/tools.ts). */
  tool?: string;
  l1: number;
  l2: number;
}

/** A hand aimed at the head: at the mouth, the chin (beard) or the chest below it, plus an offset (design px). */
export interface Anchor {
  to: 'brow' | 'mouth' | 'chin' | 'chest';
  dx?: number;
  dy?: number;
}

export interface IdleDef {
  id: string;
  group: IdleGroup;
  label: string;
  /** What it is and why it reads (body language), for the studio. */
  note: string;
  /** Keys round the loop (8–12). */
  keys: number;
  /** The weapon hand stays free (hanging), so a drawn blade can be held in it (motions/hold.ts). */
  armed: boolean;
  /** Loop length in seconds. */
  secs: number;
  /** Dropped after review (kept for the record; not in the studio, the sheets or the game). */
  dropped?: boolean;
  at: (t: number) => Partial<Moment>;
}

const TAU = Math.PI * 2;
/** A wave of `n` cycles a loop, starting at phase `p` (cycles). */
const sin = (t: number, n = 1, p = 0) => Math.sin(TAU * (n * t + p));
/** A breath: 0 out, 1 in, `n` breaths a loop. */
const br = (t: number, n = 1, p = 0) => 0.5 - 0.5 * Math.cos(TAU * (n * t + p));
/** A bump rising and falling once between `a` and `b` of the loop (0 outside). */
const bump = (t: number, a: number, b: number) =>
  t <= a || t >= b ? 0 : Math.sin((Math.PI * (t - a)) / (b - a));

const BASE: Moment = {
  w: 0,
  feet: 1,
  step: 0,
  turn: 0,
  tilt: 0,
  breath: 0,
  tuck: 1,
  rest: 'side',
  o: 0,
  lean: 0,
  headDx: 0,
  sway: 0,
  armSwing: 0,
  closed: false,
  l1: 0,
  l2: 0,
  hunch: 0,
};

export const IDLES: readonly IdleDef[] = [
  // ── a man of the jianghu: grounded, spine straight, hands purposeful ──
  {
    id: 'mRooted',
    group: 'man',
    label: 'ยืนมั่น',
    note: 'Feet wider than the hips, weight centred and sunk, hands loose a little off the body: a fighter at rest who can move any way. Two deep breaths.',
    keys: 8,
    armed: true,
    secs: 4,
    at: (t) => ({ feet: 1.5, tuck: 1, breath: 0.9 * br(t, 2), w: 0.35 + 0.15 * sin(t), step: 0.5 }),
  },
  {
    id: 'mMaster',
    group: 'man',
    label: 'ไพล่หลัง',
    note: 'Hands clasped behind, chest open, weight easy on the left leg: the composed master. A slow look to one side.',
    keys: 8,
    armed: true,
    secs: 5,
    at: (t) => ({
      rest: 'back',
      w: -0.5 - 0.2 * sin(t),
      step: 1,
      tilt: -1,
      breath: 0.6 * br(t),
      headDx: Math.round(sin(t)),
    }),
  },
  {
    id: 'mHilt',
    group: 'man',
    label: 'กุมด้ามกระบี่',
    note: 'Weight on the right leg, the left hand on the hilt at the hip, shoulder turned to it: ready to draw.',
    keys: 8,
    armed: true,
    secs: 4.5,
    at: (t) => ({
      handB: 'hilt',
      w: 0.9 + 0.2 * sin(t),
      step: 1.5,
      turn: -1,
      breath: 0.6 * br(t),
      feet: 1.1,
    }),
  },
  {
    id: 'mFold',
    group: 'man',
    label: 'กอดอก',
    note: 'Arms folded high on the chest, feet set, the weight sliding slowly from one leg to the other: waiting, unimpressed.',
    keys: 8,
    armed: false,
    secs: 5,
    at: (t) => ({
      arm: 'fold',
      far: 'fold',
      feet: 1.2,
      w: 0.7 * sin(t),
      breath: 0.5 * br(t),
      headDx: Math.round(0.6 * sin(t, 1, 0.25)),
    }),
  },
  {
    id: 'mWuji',
    group: 'man',
    label: 'ยืนปักหลัก (无极桩)',
    note: 'Standing post with the hands at the dantian, one over the other, knees well soft, eyes closed, one long breath: inward, not just standing.',
    keys: 8,
    armed: false,
    secs: 6,
    at: (t) => ({ rest: 'front', feet: 1.15, o: 2, tuck: 1, breath: 1.2 * br(t), closed: true, lean: 0 }),
  },
  {
    id: 'mBall',
    group: 'man',
    label: 'กอดลูกบอล (抱球桩)',
    note: 'Arms rounded before the chest as if holding a ball, knees bent: the qigong standing post.',
    keys: 8,
    armed: false,
    secs: 5,
    at: (t) => ({
      feet: 1.3,
      o: 2,
      // R3 (art): 'clasp' keeps the near arm before the chest head-on (the forward reach ran down the line of sight
      // and the sleeve read as a block); the turned views place it as before.
      arm: 'clasp',
      far: 'clasp',
      A: [8, 30 - br(t)],
      B: [8, 31 - br(t)],
      breath: 0.8 * br(t),
    }),
  },
  {
    id: 'mWatch',
    group: 'man',
    label: 'ระแวดระวัง (虚步)',
    note: 'Weight back on the rear leg, the front foot light, eyes sweeping left and right: on watch.',
    keys: 8,
    armed: true,
    secs: 4,
    at: (t) => ({ w: -1, step: 2.5, turn: 1, o: 1, headDx: Math.round(2 * sin(t)), breath: 0.5 * br(t, 2) }),
  },
  {
    id: 'mSash',
    group: 'man',
    label: 'ผูกสายคาด',
    note: 'Head down, both hands at the knot of the sash before the waist, tugging it tight twice, then stands tall.',
    keys: 10,
    armed: false,
    secs: 4.5,
    // R3 (QA): same silhouette as 无极桩 and กุมด้ามกระบี่ at game size; dropped.
    dropped: true,
    at: (t) => {
      const tug = bump(t, 0.15, 0.4) + bump(t, 0.45, 0.7);
      return {
        rest: 'front',
        o: Math.round(tug),
        headDx: 1,
        tilt: Math.round(2 + 3 * tug),
        feet: 1.2,
        w: 0.3,
        breath: 0.4 * br(t),
      };
    },
  },
  {
    id: 'mHip',
    group: 'man',
    label: 'เท้าสะเอว',
    note: 'Both fists on the hips, elbows out, weight on the right leg, chin up: confident.',
    keys: 8,
    armed: false,
    secs: 4,
    at: (t) => ({
      arm: 'hip',
      far: 'hip',
      feet: 1.4,
      w: 0.8 + 0.15 * sin(t),
      step: 1,
      turn: 1,
      headDx: -1,
      breath: 0.6 * br(t),
    }),
  },
  {
    id: 'mNeck',
    group: 'man',
    label: 'แกว่งแขนคลายไหล่',
    note: 'Swings the right arm round in a full circle to loosen the shoulder (甩臂), the head following, then settles.',
    keys: 12,
    armed: false,
    secs: 4,
    at: (t) => {
      const a = TAU * t;
      return {
        arm: 'forward',
        A: [2 + 10 * Math.cos(a), 24 - 10 * Math.sin(a)],
        feet: 1.2,
        headDx: Math.round(Math.cos(a)),
        breath: 0.5 * br(t),
      };
    },
  },
  {
    id: 'mGlance',
    group: 'man',
    label: 'เหลียวหลัง',
    note: 'Shoulders and head turn to look back over the shoulder, then return: something behind him.',
    keys: 8,
    armed: true,
    secs: 4.5,
    at: (t) => ({
      w: 0.6,
      step: 1,
      turn: Math.round(-4 * bump(t, 0.2, 0.8)),
      headDx: Math.round(-3 * bump(t, 0.15, 0.85)),
      breath: 0.5 * br(t),
    }),
  },
  {
    id: 'mRock',
    group: 'man',
    label: 'ยืนรอ',
    note: 'Weight rocking gently from heel to toe, hands loose: patient, alive.',
    keys: 8,
    armed: true,
    secs: 4,
    at: (t) => ({
      feet: 1.2,
      lean: Math.round(sin(t)),
      o: Math.round(0.5 + 0.5 * sin(t, 2)),
      breath: 0.6 * br(t),
    }),
  },
  // ── a woman of the jianghu: poised, feet closer, lighter weight shifts ──
  {
    id: 'fPoised',
    group: 'woman',
    label: 'ยืนสง่า',
    note: 'Feet close, one ahead, weight on the back leg, the free hand a little forward: poised.',
    keys: 8,
    armed: true,
    secs: 4.5,
    at: (t) => ({
      feet: 0.6,
      w: 1 + 0.15 * sin(t),
      step: 1,
      turn: 2,
      handB: 'forward',
      tuck: 0.6,
      breath: 0.5 * br(t),
    }),
  },
  {
    id: 'fSleeves',
    group: 'woman',
    label: 'ประสานแขนเสื้อ',
    note: 'Hands together before the waist in the sleeves, weight drifting: modest, attentive.',
    keys: 8,
    armed: true,
    secs: 5,
    at: (t) => ({ rest: 'front', feet: 0.7, w: -0.6 - 0.2 * sin(t), step: 1, breath: 0.5 * br(t) }),
  },
  {
    id: 'fHilt',
    group: 'woman',
    label: 'สองมือกุมด้ามกระบี่',
    note: 'Left hand on the scabbard at her left hip, the right hand closed over the hilt above it, shoulder turned to it, weight back. Mid-loop she sinks, turns the hilt toward the front and glances over it (thumb on the guard), then eases back: about to draw (sheathed weapon).',
    keys: 10,
    armed: false,
    secs: 4.5,
    at: (t) => {
      const ready = bump(t, 0.25, 0.75);
      return {
        rest: 'front',
        handB: 'hilt',
        feet: 0.8,
        w: -0.7 - 0.3 * ready + 0.1 * sin(t),
        step: 1.5,
        turn: Math.round(-2 - 2 * ready),
        o: Math.round(1.5 * ready),
        tilt: Math.round(-ready),
        headDx: Math.round(1 + 2 * ready),
        breath: 0.6 * br(t, 2),
      };
    },
  },
  {
    id: 'fHip',
    group: 'woman',
    label: 'เท้าสะเอว',
    note: 'Both hands on the hips, the weight sunk into one hip, the shoulders tipped against it: bold.',
    keys: 8,
    armed: false,
    secs: 4,
    at: (t) => ({
      arm: 'hip',
      far: 'hip',
      feet: 0.9,
      w: 1.2 + 0.15 * sin(t),
      step: 1.5,
      turn: 2,
      breath: 0.5 * br(t),
    }),
  },
  {
    id: 'fHair',
    group: 'woman',
    label: 'ทัดผม',
    note: 'Lifts a hand to tuck the hair behind the ear, lowers it, settles the weight.',
    keys: 10,
    armed: true,
    secs: 4.5,
    at: (t) => {
      const up = bump(t, 0.15, 0.65);
      return up > 0.2
        ? { feet: 0.7, w: 0.7, step: 1, far: 'up', B: [0, 18 - 4 * up], headDx: -1, breath: 0.4 * br(t) }
        : { feet: 0.7, w: 0.7, step: 1, breath: 0.4 * br(t) };
    },
  },
  {
    id: 'fFold',
    group: 'woman',
    label: 'กอดอก',
    note: 'Arms folded, the weight shifting from hip to hip, head tipping one way then the other, two breaths: unconvinced.',
    keys: 10,
    armed: false,
    secs: 5,
    at: (t) => ({
      arm: 'fold',
      far: 'fold',
      feet: 0.8,
      w: 0.8 * sin(t),
      step: 1,
      headDx: Math.round(1.5 * sin(t, 1, 0.1)),
      turn: Math.round(sin(t)),
      breath: 0.8 * br(t, 2),
    }),
  },
  {
    id: 'fBack',
    group: 'woman',
    label: 'ไพล่หลัง',
    note: 'Hands clasped behind, chest open, feet close: calm confidence.',
    keys: 8,
    armed: true,
    secs: 5,
    at: (t) => ({ rest: 'back', feet: 0.6, w: 0.5 + 0.2 * sin(t), step: 1, tilt: -1, breath: 0.5 * br(t) }),
  },
  {
    id: 'fWatch',
    group: 'woman',
    label: 'มองไกล',
    note: 'A hand raised to shade the eyes, scanning slowly across the horizon, weight on the back leg: watching.',
    keys: 10,
    armed: false,
    secs: 5,
    at: (t) => {
      const up = bump(t, 0.08, 0.85);
      return up > 0.2
        ? {
            arm: 'forward',
            anchorA: { to: 'brow', dx: 1 },
            feet: 0.9,
            w: -0.9,
            step: 2,
            turn: Math.round(2 * sin(t, 1, 0.1)),
            headDx: Math.round(2 * sin(t, 1, 0.1)),
            breath: 0.4 * br(t),
          }
        : { feet: 0.9, w: -0.9, step: 2, breath: 0.4 * br(t) };
    },
  },
  {
    id: 'fHeart',
    group: 'woman',
    label: 'มือแตะอก',
    note: 'One hand resting at the chest, the weight on one leg: thoughtful.',
    keys: 8,
    armed: true,
    secs: 4.5,
    at: (t) => ({
      feet: 0.7,
      w: 0.8,
      step: 1,
      far: 'clasp',
      B: [4, 29 - br(t)],
      headDx: 1,
      breath: 0.6 * br(t),
    }),
  },
  {
    id: 'fSway',
    group: 'woman',
    label: 'โยกตัวเบาๆ',
    note: 'The weight passing slowly from leg to leg, hands loose: idle, light.',
    keys: 12,
    armed: true,
    secs: 4,
    at: (t) => ({ feet: 0.7, w: 1.2 * sin(t), step: 1, breath: 0.4 * br(t, 2) }),
  },
  {
    id: 'fLook',
    group: 'woman',
    label: 'เหลียวมอง',
    note: 'Turns the head and shoulders to look aside, then back.',
    keys: 8,
    armed: true,
    secs: 4.5,
    at: (t) => ({
      feet: 0.7,
      w: 0.6,
      step: 1,
      turn: Math.round(4 * bump(t, 0.2, 0.8)),
      headDx: Math.round(3 * bump(t, 0.15, 0.85)),
      breath: 0.4 * br(t),
    }),
  },
  {
    id: 'fWuji',
    group: 'woman',
    label: 'แตะปลายเท้า (虚步)',
    note: 'All the weight sunk on the back leg, the front toe tapping the ground twice under the hem, hands together at the belly, the head following the eyes left and right: light, ready to step.',
    keys: 10,
    armed: false,
    secs: 4.5,
    at: (t) => {
      const tap = bump(t, 0.15, 0.32) + bump(t, 0.55, 0.72);
      return {
        rest: 'front',
        feet: 0.8,
        w: -0.85 - 0.15 * br(t),
        step: 1.5,
        f: [-1, 1.4 + 0.6 * tap],
        l2: Math.round(1.2 * tap),
        o: 1 + Math.round(br(t)),
        turn: 1 + Math.round(sin(t, 1, 0.25)),
        breath: 0.6 * br(t),
        headDx: Math.round(-2 * sin(t)),
      };
    },
  },
  // ── elders: the back rounded, the head forward, knees soft, a wider base, slow ──
  {
    id: 'eStoop',
    group: 'elder',
    label: 'หลังค่อมไพล่หลัง',
    note: 'Upper back rounded (not bent at the hips), head forward, hands clasped behind the back: the old man of the village square.',
    keys: 8,
    armed: false,
    secs: 6,
    at: (t) => ({
      rest: 'back',
      feet: 1.2,
      tilt: 3,
      hunch: 3,
      o: 1,
      headDx: 1,
      w: 0.3 * sin(t),
      breath: 0.5 * br(t),
    }),
  },
  {
    id: 'eCane',
    group: 'elder',
    label: 'ยืนพิงไม้เท้า',
    note: 'Both hands stacked on the crook of a walking stick planted ahead of the feet, the rounded back leaning on it, a slow nod.',
    keys: 8,
    armed: false,
    secs: 6,
    at: (t) => ({
      arm: 'forward',
      far: 'clasp',
      A: [8, 38],
      B: [8, 36],
      tool: 'cane',
      feet: 1.2,
      tilt: 4,
      hunch: 3.5,
      o: 1,
      headDx: Math.round(1 + 0.6 * sin(t, 2)),
      breath: 0.5 * br(t),
    }),
  },
  {
    id: 'eBeard',
    group: 'elder',
    label: 'ลูบเครา',
    note: 'The hand strokes down the beard from the chin to the chest and back, twice, with a slow nod; the face stays clear.',
    keys: 10,
    armed: false,
    secs: 5,
    at: (t) => ({
      arm: 'forward',
      anchorA: { to: 'chin', dx: 2, dy: 1 + 6 * br(t, 2) },
      feet: 1.1,
      tilt: 2,
      hunch: 2.5,
      o: 1,
      headDx: Math.round(1 + sin(t, 2)),
      breath: 0.4 * br(t),
    }),
  },
  {
    id: 'eStretch',
    group: 'elder',
    label: 'ยืดหลัง',
    note: 'Hands on the hips, leans back to ease the back, then settles forward again.',
    keys: 10,
    armed: false,
    secs: 6,
    at: (t) => ({
      arm: 'hip',
      far: 'hip',
      feet: 1.2,
      tilt: Math.round(3 - 8 * bump(t, 0.2, 0.7)),
      hunch: 3 - 3 * bump(t, 0.2, 0.7),
      o: 1,
      headDx: 1,
      breath: 0.6 * bump(t, 0.2, 0.7),
    }),
  },
  {
    id: 'eShift',
    group: 'elder',
    label: 'ถ่ายน้ำหนัก',
    note: 'Careful, slow weight shifts from one leg to the other, the back stooped.',
    keys: 8,
    armed: true,
    secs: 6,
    at: (t) => ({ feet: 1.3, tilt: 3, hunch: 3, o: 1, headDx: 1, w: 0.8 * sin(t), breath: 0.4 * br(t) }),
  },
  {
    id: 'eCough',
    group: 'elder',
    label: 'ไอ',
    note: 'A hand to the mouth, the body jerking forward twice with the cough, then upright again.',
    keys: 12,
    armed: false,
    secs: 5,
    at: (t) => ({
      // A fist at the side of the mouth (the jaw head-on, so the face stays clear), aimed from the head as it stands; the
      // coughs jerk the rounded back forward.
      arm: 'forward',
      anchorA: { to: 'chin', dx: 1, dy: -2 },
      feet: 1.2,
      o: 1,
      headDx: 1,
      tilt: Math.round(3 + 5 * (bump(t, 0.2, 0.35) + bump(t, 0.4, 0.55))),
      hunch: 3 + 2 * (bump(t, 0.2, 0.35) + bump(t, 0.4, 0.55)),
      closed: t > 0.2 && t < 0.55,
    }),
  },
  {
    id: 'eSky',
    group: 'elder',
    label: 'มองฟ้า',
    note: 'Straightens a little to look up at the sky, then the head sinks forward again.',
    keys: 8,
    armed: false,
    secs: 6,
    at: (t) => ({
      rest: 'back',
      feet: 1.1,
      tilt: Math.round(3 - 7 * bump(t, 0.2, 0.8)),
      hunch: 3 - 2.5 * bump(t, 0.2, 0.8),
      o: 1,
      headDx: Math.round(1 - 2 * bump(t, 0.25, 0.75)),
      breath: 0.5 * br(t),
    }),
  },
  {
    id: 'eRub',
    group: 'elder',
    label: 'ถูมือ',
    note: 'Hands rubbed together before the chest against the cold, stooped.',
    keys: 8,
    armed: false,
    secs: 4,
    at: (t) => ({
      arm: Math.round(t * 8) % 2 ? 'clapOpen' : 'clap',
      far: Math.round(t * 8) % 2 ? 'clapOpen' : 'clap',
      feet: 1.1,
      tilt: 3,
      hunch: 3,
      o: 1,
      headDx: 1,
      breath: 0.3 * br(t),
    }),
  },
  {
    id: 'eDoze',
    group: 'elder',
    label: 'สัปหงก',
    note: 'Eyes shut, the head nodding slowly forward and jerking up: dozing on his feet.',
    keys: 10,
    armed: false,
    secs: 6,
    at: (t) => ({
      rest: 'front',
      feet: 1.2,
      tilt: 3,
      hunch: 3 + 2.5 * bump(t, 0.1, 0.8),
      o: 1,
      headDx: Math.round(1 + 1.5 * bump(t, 0.1, 0.8)),
      closed: t < 0.85,
    }),
  },
  {
    id: 'eSleeves',
    group: 'elder',
    label: 'ชี้สั่งสอน',
    note: 'One hand behind the back, the other lifted and wagging a finger as he lectures, nodding: the old teacher.',
    keys: 10,
    armed: false,
    secs: 5,
    at: (t) => ({
      arm: 'forward',
      A: [9, 26 + 2 * Math.max(0, sin(t, 3))],
      far: 'back',
      feet: 1.1,
      tilt: 2,
      hunch: 2.5,
      o: 1,
      headDx: Math.round(1 + sin(t, 3)),
      breath: 0.4 * br(t),
    }),
  },
  {
    id: 'eTired',
    group: 'elder',
    label: 'ยืนพัก',
    note: 'Leaning heavily on one leg, a hand on the hip, stooped: tired.',
    keys: 8,
    armed: false,
    secs: 6,
    at: (t) => ({
      handB: 'hip',
      feet: 1.1,
      w: 1.3,
      step: 1,
      tilt: 3,
      hunch: 3,
      o: 1,
      headDx: 1,
      breath: 0.6 * br(t),
    }),
  },
  {
    id: 'eUpright',
    group: 'elder',
    label: 'ผู้เฒ่าวรยุทธ์',
    note: 'An old master: still straight, hands behind the back, chin level, a long calm breath.',
    keys: 8,
    armed: true,
    secs: 6,
    at: (t) => ({ rest: 'back', feet: 1, tilt: 3, w: -0.4, step: 1, headDx: 0, breath: 0.6 * br(t) }),
  },
  // ── children: restless, quick, never still ──
  {
    id: 'cFeet',
    group: 'child',
    label: 'ขยับเท้า',
    note: 'Shifting from foot to foot, a foot lifting each time: restless.',
    keys: 12,
    armed: false,
    secs: 3,
    at: (t) => ({
      w: 0.8 * sin(t, 2),
      l1: Math.round(2 * Math.max(0, sin(t, 2, 0.5))),
      l2: Math.round(2 * Math.max(0, sin(t, 2))),
      breath: 0.3 * br(t, 2),
    }),
  },
  {
    id: 'cSwing',
    group: 'child',
    label: 'แกว่งแขน',
    note: 'Arms swinging forward and back, the body turning with them.',
    keys: 12,
    armed: false,
    secs: 3,
    at: (t) => ({ armSwing: Math.round(3 * sin(t, 2)), turn: Math.round(sin(t, 2)), breath: 0.3 * br(t, 2) }),
  },
  {
    id: 'cRock',
    group: 'child',
    label: 'โยกส้นเท้า',
    note: 'Rocking forward over the toes and back onto the heels, knees giving at the back, arms swinging with it: plain to see at game size.',
    keys: 12,
    armed: false,
    secs: 3,
    at: (t) => {
      return {
        lean: Math.round(2 * sin(t, 2)),
        o: Math.round(1.5 * Math.max(0, -sin(t, 2))),
        armSwing: Math.round(3 * sin(t, 2)),
        breath: 0.3 * br(t, 2),
      };
    },
  },
  {
    id: 'cBehind',
    group: 'child',
    label: 'ไพล่หลังโยกตัว',
    note: 'Hands behind the back, swaying side to side: shy, waiting.',
    keys: 12,
    armed: false,
    secs: 3.5,
    at: (t) => ({ rest: 'back', w: 0.8 * sin(t, 2), turn: Math.round(sin(t, 2)), breath: 0.3 * br(t, 2) }),
  },
  {
    id: 'cLook',
    group: 'child',
    label: 'ส่องหา',
    note: 'A hand shading the eyes, the whole body turning left then right to look for someone.',
    keys: 12,
    armed: false,
    secs: 3.5,
    at: (t) => ({
      arm: 'forward',
      anchorA: { to: 'brow', dx: 1, dy: -2 },
      turn: Math.round(3 * sin(t)),
      headDx: Math.round(2 * sin(t)),
      w: 0.6 * sin(t),
      breath: 0.3 * br(t, 2),
    }),
  },
  {
    id: 'cBounce',
    group: 'child',
    label: 'เด้งตัว',
    note: 'Bouncing on the spot, knees bending and both heels lifting.',
    keys: 12,
    armed: false,
    secs: 2.5,
    at: (t) => {
      const up = Math.max(0, sin(t, 3));
      return {
        o: Math.round(2 * Math.max(0, -sin(t, 3))),
        l1: Math.round(2 * up),
        l2: Math.round(2 * up),
        armSwing: Math.round(2 * sin(t, 3)),
      };
    },
  },
  {
    id: 'cScratch',
    group: 'child',
    label: 'เกาหัว',
    note: 'A hand up scratching the head, puzzled, then down.',
    keys: 10,
    armed: false,
    secs: 3.5,
    at: (t) => {
      const up = bump(t, 0.1, 0.7);
      return up > 0.2
        ? { arm: 'forward', A: [2 + Math.round(sin(t, 4)), 6], headDx: -1, w: 0.4 }
        : { w: 0.4 };
    },
  },
  {
    id: 'cBalance',
    group: 'child',
    label: 'ทรงตัวขาเดียว',
    note: 'Arms out, standing on one leg, wobbling, then the foot comes down.',
    keys: 12,
    armed: false,
    secs: 3.5,
    at: (t) => {
      const up = bump(t, 0.1, 0.8);
      return {
        arm: up > 0.2 ? 'out' : undefined,
        far: up > 0.2 ? 'up' : undefined,
        w: up,
        l2: Math.round(5 * up),
        sway: 1.5 * sin(t, 3) * up,
        lean: Math.round(sin(t, 3) * up),
      };
    },
  },
  {
    id: 'cKick',
    group: 'child',
    label: 'เตะก้อนหิน',
    note: 'Draws the foot back, then kicks a pebble far ahead, the leg swinging up high and the arms flung back, and stands again.',
    keys: 12,
    armed: false,
    secs: 3,
    at: (t) => {
      const back = bump(t, 0.15, 0.35);
      const kick = bump(t, 0.35, 0.65);
      return {
        w: -1,
        f: [-2, Math.round(-2 - 5 * back + 13 * kick)],
        l2: Math.round(2 * back + 4 * kick),
        lean: Math.round(-2 * kick),
        armSwing: Math.round(-4 * kick + 2 * back),
        headDx: Math.round(kick),
      };
    },
  },
  {
    id: 'cCheer',
    group: 'child',
    label: 'เชียร์',
    note: 'Cheering: feet planted, the upper body swinging left and right, arms flung up and out with each swing (R3: was บิดตัว / cTwist; QA read it as a cheer).',
    keys: 12,
    armed: false,
    secs: 2.5,
    at: (t) => {
      const tw = sin(t, 2);
      return {
        feet: 1.3,
        turn: Math.round(5 * tw),
        arm: Math.abs(tw) > 0.6 ? 'out' : undefined,
        far: Math.abs(tw) > 0.6 ? 'up' : undefined,
        armSwing: Math.round(-4 * tw),
        headDx: Math.round(-tw),
        breath: 0.2 * br(t, 2),
      };
    },
  },
  {
    id: 'cClap',
    group: 'child',
    label: 'ปรบมือ',
    note: 'Clapping in small quick claps, bobbing with it.',
    keys: 12,
    armed: false,
    secs: 2.5,
    at: (t) => {
      const shut = Math.round(t * 12) % 2 === 0;
      return { arm: shut ? 'clap' : 'clapOpen', far: shut ? 'clap' : 'clapOpen', o: shut ? 1 : 0 };
    },
  },
  {
    id: 'cProud',
    group: 'child',
    label: 'เท้าสะเอวอวดเก่ง',
    note: 'Both hands on the hips, feet wide, chin up: showing off.',
    keys: 8,
    armed: false,
    secs: 3.5,
    at: (t) => ({
      arm: 'hip',
      far: 'hip',
      feet: 1.4,
      headDx: -1,
      tilt: -2,
      w: 0.3 * sin(t, 2),
      breath: 0.4 * br(t, 2),
    }),
  },
];

export type IdlePose = `idl_${string}`;
export type IdleMotion = `idl:${string}`;

/** A key's stance (for the skeleton), its own feet and its hand targets. */
export interface IdleKey {
  stance: Stance;
  inhale: true;
  feet?: [number, number];
  hands?: { A?: [number, number]; B?: [number, number] };
  anchor?: { A?: Anchor };
  tool?: string;
}

/** By key pose name: what the skeleton lays over the standing pose. */
export const IDLE_KEYS = new Map<string, IdleKey>();
/** This group's key poses (`idl_<id>_<i>`). */
export const POSES: Record<IdlePose, PoseRig<IdlePose>> = {};
/** Each idle as two motions, the halves of its loop (see the top). */
export const MOTIONS: { id: IdleMotion; keys: readonly Pose[]; loop?: false; before: Pose; after: Pose }[] =
  [];

const keyName = (id: string, i: number): IdlePose => `idl_${id}_${i}`;

/**
 * The feet for a moment, continuous through the weight passing from one leg to the other (the stance's own placing
 * jumps when the standing leg changes): both a pace apart with the weight centred, the free foot easing forward to
 * `step` as the other takes the weight.
 */
function feetOf(m: Moment): [number, number] {
  const a = Math.max(-1, Math.min(1, m.w));
  const lerp = (x: number, y: number, t: number) => x + (y - x) * t;
  return a >= 0 ? [lerp(-1, m.step, a), lerp(1, -1, a)] : [-1, lerp(1, m.step, -a)];
}

for (const d of IDLES) {
  const names: IdlePose[] = [];
  for (let i = 0; i < d.keys; i++) {
    const m: Moment = { ...BASE, ...d.at(i / d.keys) };
    const name = keyName(d.id, i);
    names.push(name);
    const a = Math.abs(m.w);
    const stance = {
      label: d.label,
      female: d.group === 'woman',
      keys: [name, name],
      motion: `idl:${d.id}:a`,
      feet: m.feet,
      ...(a > 0.02 ? { weight: m.w > 0 ? ('A' as const) : ('B' as const) } : {}),
      shift: 1.2 * a,
      shiftIn: 0,
      hipDrop: 1 * a,
      shoulderDrop: 0.6 * a,
      step: m.step * Math.min(1, a * 1.5),
      turn: m.turn,
      tilt: m.tilt,
      breath: m.breath,
      tuck: m.tuck,
      rest: m.rest,
      ...(m.handB ? { handB: m.handB } : {}),
    } as unknown as Stance;
    IDLE_KEYS.set(name, {
      stance,
      inhale: true,
      feet: m.f ?? feetOf(m),
      ...(m.A || m.B ? { hands: { ...(m.A ? { A: m.A } : {}), ...(m.B ? { B: m.B } : {}) } } : {}),
      ...(m.anchorA ? { anchor: { A: m.anchorA } } : {}),
      ...(m.tool ? { tool: m.tool } : {}),
    });
    const rig: Partial<Rig> = {
      o: m.o,
      lean: m.lean,
      headDx: m.headDx,
      sway: m.sway,
      armSwing: m.armSwing,
      closed: m.closed,
      l1: m.l1,
      l2: m.l2,
      hunch: m.hunch,
      ...(m.arm ? { arm: m.arm } : {}),
      ...(m.far ? { far: m.far } : {}),
    };
    POSES[name] = () => rig;
  }
  const half = Math.floor(d.keys / 2);
  const n = d.keys;
  MOTIONS.push(
    { id: `idl:${d.id}:a`, keys: names.slice(0, half + 1), before: names[n - 1]!, after: names[half + 1]! },
    {
      id: `idl:${d.id}:b`,
      keys: [...names.slice(half), names[0]!],
      before: names[half - 1]!,
      after: names[1]!,
    },
  );
}

/** The idles of a group. */
export const idlesOf = (g: IdleGroup): IdleDef[] => IDLES.filter((d) => d.group === g && !d.dropped);

/** Frames in one loop of an idle (the two halves, the join frame shown once each way). */
export const IDLE_LOOP_FRAMES = 26;

/** Typed check that the motions fit the shared shape. */
export const _check: readonly MotionDef[] = MOTIONS;
